import { CreditCardProductCategory } from '@appTypes/EligibilityApi.interface'

type Pros = {
    heading: string
    text: string
}

export const prosList: Record<Partial<CreditCardProductCategory>, Pros[]> = {
    [CreditCardProductCategory.BalanceTransfer]: [
        {
            heading: 'Massive interest savings',
            text: 'Balance transfers let you save hundreds, if not thousands of pounds. Just remember that the best offers are usually available to those with the best credit scores.',
        },

        {
            heading: 'Long interest-free periods',
            text: 'The best deals currently offer almost three years interest-free, giving you extra breathing space to clear your debt.',
        },

        {
            heading: 'Simple debt management',
            text: 'Combine card balances from different cards into one monthly payment can make it easier to track and manage your debt.',
        },

        {
            heading: 'Break the interest cycle',
            text: 'Stop spending money on interest payments, and put every penny towards clearing the actual debt.',
        },

        {
            heading: 'Extra protection',
            text: 'Like other credit cards, balance transfers come with Section 75 protection on certain purchases over £100.',
        },
    ],

    [CreditCardProductCategory.Cashback]: [
        {
            heading: 'Real money back',
            text: 'Get cash back instead of points or vouchers, giving you the freedom to spend it how and where you like.',
        },

        {
            heading: 'Simple to understand',
            text: 'Points systems can be confusing and often come with expiry dates.  With cashback you earn a percentage of what you spend.',
        },

        {
            heading: 'Works on everyday spending',
            text: 'Get cashback on everyday purchases and spending like groceries, fuel and bills.',
        },

        {
            heading: 'Section 75 protection',
            text: 'Eligible purchases between £100-£30,000 are protected if something goes wrong.',
        },

        {
            heading: 'Good for regular spenders',
            text: 'If you use your card regularly, the cashback can add up over time.',
        },

        {
            heading: 'No restrictions',
            text: 'Unlike rewards cards that use points or vouchers, cashback can be used anywhere or saved.',
        },
    ],

    [CreditCardProductCategory.Combined]: [
        {
            heading: 'Two cards in one',
            text: 'Shift debt you’re paying interest on, and make new purchases on the same card, making it simpler to manage your finances.',
        },

        {
            heading: 'Stop paying interest on debt',
            text: 'Transferring balances to a 0% card means more of your money goes towards clearing what you owe, and not paying interest.',
        },

        {
            heading: 'Interest-free spending',
            text: 'Spread the cost of purchases without paying interest during the promotional period.',
        },

        {
            heading: 'One monthly payment',
            text: 'Make just one payment each month, instead of juggling multiple cards.',
        },

        {
            heading: 'Section 75',
            text: 'Eligible purchases between £100-£30,000 are protected if something goes wrong.',
        },

        {
            heading: 'Smart payments',
            text: 'The card automatically pays off the balance with the higher rate first, reducing how much interest you pay.',
        },
    ],

    [CreditCardProductCategory.PoorCredit]: [
        {
            heading: 'Helps rebuild your credit score',
            text: 'Use a credit card for bad credit responsibly, and you could see improvements within 6-12 months.',
        },

        {
            heading: 'Easier to get accepted',
            text: 'Specialist lenders have designed these cards especially for people with defaults, CCJs, imperfect credit files, or previous rejections.',
        },

        {
            heading: 'Payment protection',
            text: 'Credit cards come with Section 75 protection which offers protection on certain purchases over £100.',
        },

        {
            heading: 'You won’t pay interest if paid in full',
            text: 'Pay off your balance in full each month, and you’ll never pay a penny in interest on your purchases, no matter what the APR is.',
        },

        {
            heading: 'Helps you to build better financial habits',
            text: 'Using a credit card for bad credit responsibly can help you budget and manage repayments, creating better money management skills for the future.',
        },
    ],

    [CreditCardProductCategory.Purchase]: [
        {
            heading: 'Interest-free spending',
            text: 'Buy what you need and pay it off over a set number of months without interest payments eating into your budget.',
        },

        {
            heading: 'Extended 0% periods',
            text: 'The best deals often offer interest-free periods of between 1-2 years, giving you plenty of time to clear the cost of your purchases.',
        },

        {
            heading: 'Better cash flow management',
            text: 'Spread the cost of big purchases, helping you manage your money more easily.',
        },

        {
            heading: 'Section 75 protection',
            text: 'With credit cards, certain purchases between £100-£30,000 are protected under Section 75, giving you additional consumer rights should anything go wrong.',
        },

        {
            heading: 'Build your credit score',
            text: 'Using the card responsibly can help improve your credit rating.',
        },
    ],

    [CreditCardProductCategory.Rewards]: [
        {
            heading: 'Earn while you spend',
            text: 'Get cashback or points on purchases you’d make anyway, turning everyday spending into real rewards.',
        },

        {
            heading: 'Section 75 protection',
            text: 'Eligible purchases between £100-£30,000 are protected if something goes wrong, with your card provider sharing responsibility with the seller.',
        },

        {
            heading: 'Build your credit score',
            text: 'Using the card responsibly and paying it off each month can show lenders you’re able to manage credit, which can improve your credit rating.',
        },

        {
            heading: 'Extra perks',
            text: 'Some cards offer benefits like airport lounge access, travel insurance, or exclusive shopping discounts.',
        },

        {
            heading: 'Works for big spenders',
            text: 'If you put a lot through your card each month and pay it off in full, the rewards can really add up.',
        },
    ],

    [CreditCardProductCategory.Travel]: [
        {
            heading: 'Avoid foreign transaction fees',
            text: 'Save money when you spend by avoiding foreign transaction fees that you might otherwise pay on your regular card.',
        },

        {
            heading: 'Better exchange rates',
            text: "Get the card provider's exchange rate without extra mark-ups, helping your money go further.",
        },

        {
            heading: 'Section 75 protection',
            text: 'Eligible purchases between £100-£30,000 are protected if something goes wrong with your booking or purchase.',
        },

        {
            heading: 'Safer than cash',
            text: 'If your card is lost or stolen, you can cancel it. With cash, the money’s gone.',
        },

        {
            heading: 'Fee-free withdrawals on some cards',
            text: 'Some cards let you withdraw money from an ATM without fees, though interest usually applies immediately.',
        },

        {
            heading: 'Good for online shopping',
            text: 'Use them to buy from foreign websites without paying extra charges.',
        },
    ],

    // TODO: Add pros for other categories when the data is ready
    [CreditCardProductCategory.BestCards]: [],
}
